"use strict";
/**
 * Outline map UI prefs — local + server dogfood.
 *
 * Global (`audroam.outlineMap.prefs`): zoom, split, dark, structure chrome, default map height.
 * Per-row local (`audroam.outlineMap.rowStyle:{personId}:{rowId}`): mode, mapOn, mapHeightPx, …
 * Server: pilot_notes.pnDisplayStyle (JSON) via POST /pilot-notes/{id}/fields — multi-device.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.flushDisplayStyleServerSync = exports.scheduleDisplayStyleServerSync = exports.mergeServerDisplayStyleIntoLocal = exports.styleToServerJson = exports.parseDisplayStyleFromRow = exports.applyRowDisplayStyleToEdit = exports.saveRowBodyDisplayStyle = exports.loadRowBodyDisplayStyle = exports.normalizeDisplayMode = exports.rowStyleStorageKey = exports.saveMapPrefs = exports.loadMapPrefs = exports.defaultMapPrefs = exports.resolvePersonIdFromScope = exports.normalizeRowId = exports.normalizePersonId = exports.clampBodyRows = exports.clampMapHeight = exports.MAP_HEIGHT_PRESETS = exports.MAP_HEIGHT_STEP = exports.MAP_HEIGHT_DEFAULT = exports.MAP_HEIGHT_MAX = exports.MAP_HEIGHT_MIN = exports.OUTLINE_ROW_STYLE_PREFIX = exports.OUTLINE_MAP_PREFS_KEY = void 0;
exports.OUTLINE_MAP_PREFS_KEY = 'audroam.outlineMap.prefs';
exports.OUTLINE_ROW_STYLE_PREFIX = 'audroam.outlineMap.rowStyle:';
exports.MAP_HEIGHT_MIN = 140;
exports.MAP_HEIGHT_MAX = 720;
exports.MAP_HEIGHT_DEFAULT = 280;
exports.MAP_HEIGHT_STEP = 40;
exports.MAP_HEIGHT_PRESETS = [180, 280, 400, 560];
var ZOOM_MIN = 0.7;
var ZOOM_MAX = 1.8;
function clampMapHeight(px) {
    var n = Math.round(Number(px) || exports.MAP_HEIGHT_DEFAULT);
    return Math.min(exports.MAP_HEIGHT_MAX, Math.max(exports.MAP_HEIGHT_MIN, n));
}
exports.clampMapHeight = clampMapHeight;
function clampBodyRows(n) {
    var v = Math.round(Number(n) || 12);
    if (v >= 30)
        return 30;
    if (v <= 12)
        return 12;
    return 20;
}
exports.clampBodyRows = clampBodyRows;
function normalizePersonId(personId) {
    if (personId == null || personId === '')
        return 'anon';
    return String(personId);
}
exports.normalizePersonId = normalizePersonId;
function normalizeRowId(rowId) {
    if (rowId == null || rowId === '' || rowId === 0 || rowId === '0')
        return null;
    return String(rowId);
}
exports.normalizeRowId = normalizeRowId;
/**
 * Walk Angular scopes / plain objects for person primary key.
 * Prefer dbPK (draft storage), then personId / ID / id.
 */
function resolvePersonIdFromScope(start) {
    var _a, _b, _c, _d;
    try {
        var s = start;
        var hops = 0;
        while (s && hops < 40) {
            var p = s.person;
            if (p && typeof p === 'object') {
                var id = (_d = (_c = (_b = (_a = p.dbPK) !== null && _a !== void 0 ? _a : p.personId) !== null && _b !== void 0 ? _b : p.ID) !== null && _c !== void 0 ? _c : p.id) !== null && _d !== void 0 ? _d : p.PersonPK;
                if (id != null && id !== '')
                    return normalizePersonId(id);
            }
            if (s.personId != null && s.personId !== '') {
                return normalizePersonId(s.personId);
            }
            s = s.$parent;
            hops++;
        }
    }
    catch (_e) {
        /* ignore */
    }
    return 'anon';
}
exports.resolvePersonIdFromScope = resolvePersonIdFromScope;
function defaultMapPrefs(nightMode) {
    return {
        dark: !!nightMode,
        zoom: 1,
        textPct: 42,
        structChrome: null,
        mapHeightPx: exports.MAP_HEIGHT_DEFAULT,
    };
}
exports.defaultMapPrefs = defaultMapPrefs;
function loadMapPrefs() {
    var night = typeof document !== 'undefined' &&
        document.documentElement.classList.contains('night-mode');
    var defaults = defaultMapPrefs(night);
    try {
        var raw = window.localStorage.getItem(exports.OUTLINE_MAP_PREFS_KEY);
        if (!raw)
            return defaults;
        var p = JSON.parse(raw);
        var zoom = typeof p.zoom === 'number' ? p.zoom : 1;
        var textPct = typeof p.textPct === 'number' ? p.textPct : 42;
        var structChrome = typeof p.structChrome === 'boolean' ? p.structChrome : null;
        var mapHeightPx = typeof p.mapHeightPx === 'number' ? p.mapHeightPx : exports.MAP_HEIGHT_DEFAULT;
        return {
            dark: !!p.dark || night,
            zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)),
            textPct: Math.min(72, Math.max(12, textPct)),
            structChrome: structChrome,
            mapHeightPx: clampMapHeight(mapHeightPx),
        };
    }
    catch (_a) {
        return defaults;
    }
}
exports.loadMapPrefs = loadMapPrefs;
function saveMapPrefs(prefs) {
    try {
        var cur = loadMapPrefs();
        var next = {
            dark: prefs.dark != null ? !!prefs.dark : cur.dark,
            zoom: typeof prefs.zoom === 'number'
                ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prefs.zoom))
                : cur.zoom,
            textPct: typeof prefs.textPct === 'number'
                ? Math.min(72, Math.max(12, prefs.textPct))
                : cur.textPct,
            structChrome: prefs.structChrome === true ||
                prefs.structChrome === false ||
                prefs.structChrome === null
                ? prefs.structChrome
                : cur.structChrome,
            mapHeightPx: typeof prefs.mapHeightPx === 'number'
                ? clampMapHeight(prefs.mapHeightPx)
                : cur.mapHeightPx,
        };
        window.localStorage.setItem(exports.OUTLINE_MAP_PREFS_KEY, JSON.stringify(next));
    }
    catch (_a) {
        /* ignore */
    }
}
exports.saveMapPrefs = saveMapPrefs;
function rowStyleStorageKey(personId, rowId) {
    var rid = normalizeRowId(rowId);
    if (!rid)
        return null;
    return "".concat(exports.OUTLINE_ROW_STYLE_PREFIX).concat(normalizePersonId(personId), ":").concat(rid);
}
exports.rowStyleStorageKey = rowStyleStorageKey;
function normalizeDisplayMode(mode) {
    if (mode === 'text' || mode === 'caption' || mode === 'outline')
        return mode;
    return 'outline';
}
exports.normalizeDisplayMode = normalizeDisplayMode;
function parseRowStyle(raw) {
    try {
        var p = JSON.parse(raw);
        if (!p || typeof p !== 'object')
            return null;
        var mode = normalizeDisplayMode(p.mode);
        var out = { mode: mode };
        if (typeof p.caption === 'string') {
            var cap = p.caption.trim();
            if (cap)
                out.caption = cap.slice(0, 500);
        }
        if (p.mapOn === true || p.mapOn === false) {
            out.mapOn = p.mapOn;
        }
        else if (p.mapOn === null) {
            out.mapOn = null;
        }
        if (typeof p.mapHeightPx === 'number') {
            out.mapHeightPx = clampMapHeight(p.mapHeightPx);
        }
        if (typeof p.bodyRows === 'number' && p.bodyRows > 0) {
            out.bodyRows = clampBodyRows(p.bodyRows);
        }
        if (typeof p.textPct === 'number') {
            out.textPct = Math.min(72, Math.max(12, p.textPct));
        }
        if (typeof p.updatedAt === 'string') {
            out.updatedAt = p.updatedAt;
        }
        return out;
    }
    catch (_a) {
        return null;
    }
}
function loadRowBodyDisplayStyle(personId, rowId) {
    var key = rowStyleStorageKey(personId, rowId);
    if (!key)
        return null;
    try {
        var raw = window.localStorage.getItem(key);
        // Migrate/fallback: older saves under anon when person was unresolved
        if (!raw && normalizePersonId(personId) !== 'anon') {
            var anonKey = rowStyleStorageKey('anon', rowId);
            if (anonKey) {
                raw = window.localStorage.getItem(anonKey);
                if (raw) {
                    // Promote to person-scoped key
                    try {
                        window.localStorage.setItem(key, raw);
                    }
                    catch (_a) {
                        /* ignore */
                    }
                }
            }
        }
        if (!raw)
            return null;
        return parseRowStyle(raw);
    }
    catch (_b) {
        return null;
    }
}
exports.loadRowBodyDisplayStyle = loadRowBodyDisplayStyle;
function saveRowBodyDisplayStyle(personId, rowId, style) {
    var key = rowStyleStorageKey(personId, rowId);
    if (!key)
        return;
    try {
        var prev = loadRowBodyDisplayStyle(personId, rowId) ||
            { mode: 'outline' };
        var next = {
            mode: style.mode === 'text' ||
                style.mode === 'outline' ||
                style.mode === 'caption'
                ? style.mode
                : prev.mode || 'outline',
            // Preserve caller updatedAt when seeding from server; else stamp now.
            updatedAt: typeof style.updatedAt === 'string' && style.updatedAt
                ? style.updatedAt
                : new Date().toISOString(),
        };
        if (style.mapOn === true || style.mapOn === false || style.mapOn === null) {
            next.mapOn = style.mapOn;
        }
        else if (prev.mapOn === true || prev.mapOn === false || prev.mapOn === null) {
            next.mapOn = prev.mapOn;
        }
        var mh = style.mapHeightPx != null ? style.mapHeightPx : prev.mapHeightPx;
        if (typeof mh === 'number')
            next.mapHeightPx = clampMapHeight(mh);
        var br = style.bodyRows != null ? style.bodyRows : prev.bodyRows;
        if (typeof br === 'number' && br > 0)
            next.bodyRows = clampBodyRows(br);
        var tp = style.textPct != null ? style.textPct : prev.textPct;
        if (typeof tp === 'number')
            next.textPct = Math.min(72, Math.max(12, tp));
        if (typeof style.caption === 'string') {
            var cap = style.caption.trim();
            if (cap)
                next.caption = cap.slice(0, 500);
        }
        else if (style.caption !== '') {
            if (typeof prev.caption === 'string' && prev.caption.trim()) {
                next.caption = prev.caption.trim().slice(0, 500);
            }
        }
        window.localStorage.setItem(key, JSON.stringify(next));
    }
    catch (_a) {
        /* ignore */
    }
}
exports.saveRowBodyDisplayStyle = saveRowBodyDisplayStyle;
/**
 * Apply stored style when opening edit: bodyRows on row.edit.
 * Returns the style for further use (map height / mapOn).
 */
function applyRowDisplayStyleToEdit(personId, rowId, edit) {
    var style = loadRowBodyDisplayStyle(personId, rowId);
    if (!style || !edit)
        return style;
    if (typeof style.bodyRows === 'number') {
        edit.bodyRows = style.bodyRows;
    }
    return style;
}
exports.applyRowDisplayStyleToEdit = applyRowDisplayStyleToEdit;
/** Parse pnDisplayStyle from a recent/search row (string JSON or object). */
function parseDisplayStyleFromRow(row) {
    if (!row)
        return null;
    var raw = row.pnDisplayStyle != null ? row.pnDisplayStyle : row.displayStyle;
    if (raw == null || raw === '')
        return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return parseRowStyle(JSON.stringify(raw));
    }
    if (typeof raw === 'string') {
        return parseRowStyle(raw);
    }
    return null;
}
exports.parseDisplayStyleFromRow = parseDisplayStyleFromRow;
/** Compact JSON string for POST /fields pnDisplayStyle. */
function styleToServerJson(style) {
    var clean = {
        mode: normalizeDisplayMode(style.mode),
        updatedAt: style.updatedAt || new Date().toISOString(),
    };
    if (typeof style.caption === 'string' && style.caption.trim()) {
        clean.caption = style.caption.trim().slice(0, 500);
    }
    if (style.mapOn === true || style.mapOn === false)
        clean.mapOn = style.mapOn;
    if (typeof style.mapHeightPx === 'number') {
        clean.mapHeightPx = clampMapHeight(style.mapHeightPx);
    }
    if (typeof style.bodyRows === 'number')
        clean.bodyRows = clampBodyRows(style.bodyRows);
    if (typeof style.textPct === 'number') {
        clean.textPct = Math.min(72, Math.max(12, style.textPct));
    }
    return JSON.stringify(clean);
}
exports.styleToServerJson = styleToServerJson;
/**
 * Seed localStorage from server row when server is newer or local missing.
 * Prefer local if local.updatedAt is strictly after server.updatedAt.
 */
function mergeServerDisplayStyleIntoLocal(personId, rowId, row) {
    var server = parseDisplayStyleFromRow(row);
    if (!server)
        return loadRowBodyDisplayStyle(personId, rowId);
    var local = loadRowBodyDisplayStyle(personId, rowId);
    if (!local) {
        saveRowBodyDisplayStyle(personId, rowId, server);
        return loadRowBodyDisplayStyle(personId, rowId);
    }
    var localT = local.updatedAt ? Date.parse(local.updatedAt) : 0;
    var serverT = server.updatedAt ? Date.parse(server.updatedAt) : 0;
    if (!localT || (serverT && serverT >= localT)) {
        saveRowBodyDisplayStyle(personId, rowId, server);
        return loadRowBodyDisplayStyle(personId, rowId);
    }
    return local;
}
exports.mergeServerDisplayStyleIntoLocal = mergeServerDisplayStyleIntoLocal;
var serverSyncTimers = {};
var SERVER_SYNC_DEBOUNCE_MS = 800;
/**
 * Debounced push of full row display style to pilot_notes.pnDisplayStyle.
 * No-op if rowId invalid or patchFields missing.
 */
function scheduleDisplayStyleServerSync(personId, rowId, patchFields) {
    var rid = normalizeRowId(rowId);
    if (!rid || !patchFields)
        return;
    var idNum = Number(rid);
    if (!idNum || idNum <= 0)
        return;
    var key = "".concat(normalizePersonId(personId), ":").concat(rid);
    if (serverSyncTimers[key]) {
        clearTimeout(serverSyncTimers[key]);
    }
    serverSyncTimers[key] = setTimeout(function () {
        delete serverSyncTimers[key];
        var style = loadRowBodyDisplayStyle(personId, rowId);
        if (!style)
            return;
        try {
            var payload = styleToServerJson(style);
            var p = patchFields(idNum, { pnDisplayStyle: payload });
            if (p && typeof p.then === 'function') {
                p.then(function () {
                    /* ok */
                }, function () {
                    /* offline / pre-migration — local still holds style */
                });
            }
        }
        catch (_a) {
            /* ignore */
        }
    }, SERVER_SYNC_DEBOUNCE_MS);
}
exports.scheduleDisplayStyleServerSync = scheduleDisplayStyleServerSync;
/** Immediate flush (edit close / pagehide). */
function flushDisplayStyleServerSync(personId, rowId, patchFields) {
    var rid = normalizeRowId(rowId);
    if (!rid)
        return;
    var key = "".concat(normalizePersonId(personId), ":").concat(rid);
    if (serverSyncTimers[key]) {
        clearTimeout(serverSyncTimers[key]);
        delete serverSyncTimers[key];
    }
    if (!patchFields)
        return;
    var idNum = Number(rid);
    if (!idNum || idNum <= 0)
        return;
    var style = loadRowBodyDisplayStyle(personId, rowId);
    if (!style)
        return;
    try {
        patchFields(idNum, { pnDisplayStyle: styleToServerJson(style) });
    }
    catch (_a) {
        /* ignore */
    }
}
exports.flushDisplayStyleServerSync = flushDisplayStyleServerSync;
